import { setComponents, StoryblokComponent, useStoryblokBridge } from '@storyblok/react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import StoryblokComponents from '../../components/Storyblok';
import config from '../../config';
import useAppSelector from '../../util/hooks/useAppSelector';
import { STORY_EXTRA_ALTERNATES } from '../../util/fixedLocalePages';
import { localizedAlternateLinks } from '../../util/seo';
import NotFoundPage from '../NotFoundPage/LoadableNotFoundPage';
import StoryblokErrorPage from './StoryblokErrorPage';
import StoryblokLoadingPage from './StoryblokLoadingPage';
import { bridgeUpdateStoryblokStory } from './StoryblokPage.duck';
import StoryPageLayout from './StoryPageLayout';
import storyPageMeta from './storyPageMeta';

const HEYFLOW_SCRIPT = 'https://static.heyflow.com/widget/latest/webview.js';

/**
 * Props for `Page` of a Storyblok story rendered at `pathname` in `locale`.
 *
 * hreflang alternates: the current locale (self) plus every PUBLISHED translation,
 * each using its own translated slug. Drafts are omitted — those URLs 404 by design.
 */
const storyPageProps = (story, locale, pathname) => ({
  ...storyPageMeta(story),
  customScripts: story.hasHeyFlow ? [HEYFLOW_SCRIPT] : [],
  alternateLinks: localizedAlternateLinks({
    translatedSlugs: story.translated_slugs,
    defaultSlug: story.default_full_slug,
    defaultLocale: config.locale,
    currentLocale: locale,
    selfHref: `${config.canonicalRootURL}${pathname}`,
    canonicalRootURL: config.canonicalRootURL,
    extraAlternates: STORY_EXTRA_ALTERNATES[story.default_full_slug],
  }),
});

/**
 * What StoryblokPage shows for the current fetch state: 'loading', 'notFound', 'error' or 'ready'.
 */
const storyState = ({ currentStory, fetchingInProgress, error }) => {
  if (error) {
    return error.status === 404 ? 'notFound' : 'error';
  }
  return !currentStory || fetchingInProgress ? 'loading' : 'ready';
};

const StoryblokPage = () => {
  setComponents(StoryblokComponents);

  const { currentStory, fetchingInProgress, error } = useAppSelector(
    (state) => ({
      currentStory: state.StoryblokPage.currentStory,
      fetchingInProgress: state.StoryblokPage.fetchingInProgress,
      error: state.StoryblokPage.error,
    }),
    shallowEqual,
  );
  const pageState = storyState({ currentStory, fetchingInProgress, error });
  const location = useLocation();
  const dispatch = useDispatch();
  const intl = useIntl();

  const pageProps = useMemo(
    () => currentStory && storyPageProps(currentStory, intl.locale, location.pathname),
    [currentStory, intl.locale, location.pathname],
  );

  if (
    typeof window !== 'undefined' &&
    process.env.REACT_APP_ENV === 'development' &&
    currentStory
  ) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useStoryblokBridge(currentStory.id, (story) =>
      dispatch(bridgeUpdateStoryblokStory(story, new URLSearchParams(location.search), intl.locale)),
    );
  }

  if (pageState === 'loading') {
    return <StoryblokLoadingPage />;
  }

  if (pageState === 'notFound') {
    return <NotFoundPage />;
  }

  if (pageState === 'error') {
    return <StoryblokErrorPage />;
  }

  return (
    <StoryPageLayout pageProps={pageProps}>
      <StoryblokComponent blok={currentStory.content} story={currentStory} />
    </StoryPageLayout>
  );
};

export default StoryblokPage;
